import { gqlClient } from '@/api/graphql-client';
import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { graphql } from '@/gql';
import {
  DeleteAutomationMutation,
  GetPipelineStageTargetingAutomationsQuery,
} from '@/gql/graphql';
import {
  InfiniteData,
  UseMutationOptions,
  hashKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { DRAFT_MUTATION_SCOPE_ID } from '../../context/DraftModeContext';
import draftMutationKeys from './keyFactory';
import { applyOptimisticDeleteAutomation } from './optimisticUpdates';

const DELETE_AUTOMATION_MUTATION = graphql(`
  mutation DeleteAutomation($id: ID!) {
    deleteAutomation(id: $id)
  }
`);

type DeleteAutomationInput = {
  automationId: string;
};

type UseDraftDeleteAutomationArgs = {
  pipelinePublicId: string;
  pipelineStagePublicId: string;
  options?: UseMutationOptions<
    DeleteAutomationMutation,
    unknown,
    DeleteAutomationInput
  >;
};

type MutationContext = {
  previousData?: InfiniteData<GetPipelineStageTargetingAutomationsQuery>;
};

const useDraftDeleteAutomation = (args: UseDraftDeleteAutomationArgs) => {
  const { pipelinePublicId, pipelineStagePublicId, options } = args;
  const queryClient = useQueryClient();

  return useMutation<
    DeleteAutomationMutation,
    unknown,
    DeleteAutomationInput,
    MutationContext
  >({
    ...options,
    mutationKey: draftMutationKeys.deleteAutomation,
    mutationFn: (input: DeleteAutomationInput) => {
      return gqlClient.request(DELETE_AUTOMATION_MUTATION, {
        id: input.automationId,
      });
    },
    scope: { id: DRAFT_MUTATION_SCOPE_ID },
    onMutate: async (variables) => {
      const queryKey = pipelineKeys.pipelineStageTargetingAutomations(
        pipelinePublicId,
        pipelineStagePublicId
      );

      // Cancel out unpersisted create+delete pairs
      if (variables.automationId.startsWith('optimistic-')) {
        const mutationCache = queryClient.getMutationCache();
        const createMutationKeyHash = hashKey(
          draftMutationKeys.createAutomation
        );
        const matchingCreate = mutationCache
          .getAll()
          .find(
            (m) =>
              m.state.isPaused &&
              m.options.mutationKey &&
              hashKey(m.options.mutationKey) === createMutationKeyHash &&
              (m.state.context as { optimisticId?: string } | undefined)
                ?.optimisticId === variables.automationId
          );

        if (matchingCreate) {
          // Remove the paused create mutation so it never fires
          mutationCache.remove(matchingCreate);

          // Apply the optimistic delete so the UI removes the item
          await queryClient.cancelQueries({ queryKey });
          const previousData =
            queryClient.getQueryData<
              InfiniteData<GetPipelineStageTargetingAutomationsQuery>
            >(queryKey);

          if (previousData) {
            queryClient.setQueryData<
              InfiniteData<GetPipelineStageTargetingAutomationsQuery>
            >(
              queryKey,
              applyOptimisticDeleteAutomation(
                previousData,
                variables.automationId
              )
            );
          }

          // Remove this delete mutation itself (it's the last entry in the cache)
          const allMutations = mutationCache.getAll();
          const thisMutation = allMutations[allMutations.length - 1];
          if (thisMutation) {
            mutationCache.remove(thisMutation);
          }

          void options?.onMutate?.(variables);
          return { previousData };
        }
      }

      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<
          InfiniteData<GetPipelineStageTargetingAutomationsQuery>
        >(queryKey);

      if (previousData) {
        queryClient.setQueryData<
          InfiniteData<GetPipelineStageTargetingAutomationsQuery>
        >(
          queryKey,
          applyOptimisticDeleteAutomation(previousData, variables.automationId)
        );
      }

      void options?.onMutate?.(variables);
      return { previousData };
    },
    onError: (err, variables, context) => {
      const previousData = context?.previousData;

      if (previousData) {
        queryClient.setQueryData(
          pipelineKeys.pipelineStageTargetingAutomations(
            pipelinePublicId,
            pipelineStagePublicId
          ),
          previousData
        );
      }

      options?.onError?.(err, variables, context);
    },
  });
};

export default useDraftDeleteAutomation;
